import { useEffect, useId, useRef, useState } from "react";

type AuthConfig = {
  captcha_required?: boolean;
  hcaptcha_enabled?: boolean;
  hcaptcha_site_key?: string;
  captcha_config_error?: string | null;
};

type Props = {
  /** Called whenever the captcha token changes (including clear/expire). */
  onToken: (token: string) => void;
  /** Bump to force widget reset after a failed submit. */
  resetKey?: number;
  /** Which env flag gates this widget: login (default) or homepage. */
  purpose?: "login" | "homepage";
};

declare global {
  interface Window {
    hcaptcha?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        },
      ) => string | number;
      reset: (widgetId?: string | number) => void;
      remove?: (widgetId?: string | number) => void;
    };
    onTnkHCaptchaLoad?: () => void;
  }
}

const SCRIPT_SRC = "https://js.hcaptcha.com/1/api.js?render=explicit&onload=onTnkHCaptchaLoad";

let scriptLoading: Promise<void> | null = null;

function loadHcaptchaScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.hcaptcha?.render) return Promise.resolve();
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-tnk-hcaptcha]");
    if (existing) {
      window.onTnkHCaptchaLoad = () => resolve();
      if (window.hcaptcha?.render) resolve();
      return;
    }
    window.onTnkHCaptchaLoad = () => resolve();
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.dataset.tnkHcaptcha = "1";
    script.onerror = () => reject(new Error("Failed to load hCaptcha"));
    document.head.appendChild(script);
  });
  return scriptLoading;
}

export function HCaptchaField({ onToken, resetKey = 0, purpose = "login" }: Props) {
  const mountId = useId();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | number | null>(null);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const qs = purpose === "homepage" ? "?purpose=homepage" : "?purpose=login";
    fetch(`/api/customers/auth-config${qs}`, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((data: AuthConfig) => {
        if (!cancelled) setConfig(data);
      })
      .catch(() => {
        if (!cancelled) {
          setConfig({ captcha_required: false, hcaptcha_enabled: false });
          setLoadError("Could not load captcha settings.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [purpose]);

  useEffect(() => {
    if (!config?.hcaptcha_enabled || !config.hcaptcha_site_key) {
      onToken("");
      return;
    }

    let cancelled = false;
    const siteKey = config.hcaptcha_site_key;

    loadHcaptchaScript()
      .then(() => {
        if (cancelled || !hostRef.current || !window.hcaptcha?.render) return;
        if (widgetIdRef.current !== null && window.hcaptcha.remove) {
          try {
            window.hcaptcha.remove(widgetIdRef.current);
          } catch {
            /* ignore */
          }
          widgetIdRef.current = null;
        }
        hostRef.current.innerHTML = "";
        widgetIdRef.current = window.hcaptcha.render(hostRef.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token || ""),
          "expired-callback": () => onToken(""),
          "error-callback": () => {
            onToken("");
            setLoadError("Captcha failed to load. Please refresh and try again.");
          },
        });
        setLoadError(null);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError("Captcha failed to load. Please refresh and try again.");
          onToken("");
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onToken is stable enough via parent state setter
  }, [config?.hcaptcha_enabled, config?.hcaptcha_site_key]);

  useEffect(() => {
    if (resetKey === 0) return;
    onToken("");
    if (widgetIdRef.current !== null && window.hcaptcha?.reset) {
      try {
        window.hcaptcha.reset(widgetIdRef.current);
      } catch {
        /* ignore */
      }
    }
  }, [resetKey, onToken]);

  if (!config) {
    return (
      <div className="buy-server-row hcaptcha-row">
        <span className="muted">Loading captcha…</span>
      </div>
    );
  }

  if (config.captcha_config_error) {
    return (
      <div className="buy-server-row hcaptcha-row">
        <p className="field-error" role="alert">
          {config.captcha_config_error}
        </p>
      </div>
    );
  }

  if (!config.hcaptcha_enabled) {
    return null;
  }

  return (
    <div className="buy-server-row hcaptcha-row">
      <label htmlFor={mountId}>
        Captcha <span className="req-star">*</span>
      </label>
      <div className="buy-server-control hcaptcha-wrap">
        <div id={mountId} ref={hostRef} className="hcaptcha-host" />
        {loadError ? (
          <p className="field-error" role="alert">
            {loadError}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function captchaRequiredFromConfig(config: AuthConfig | null): boolean {
  return Boolean(config?.captcha_required && config?.hcaptcha_enabled);
}
